import { useEffect } from 'react';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  FieldOption,
  OperatorOption,
  LogicalOperator,
} from '@/components/publicSearch/constants';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetSalesLazyQuery } from '@/publicSearch/graphql/queries.generated';
import type {
  ComparisonOperator,
  FilterGroupOfSaleFilteredFilterOptionInput,
  SaleFilterField,
} from '@/graphqlClient/types';

export function useGetSaleDetails({
  orgId,
  saleId,
}: {
  orgId?: string | null;
  saleId?: string | null;
}) {
  const [getSales, { data, loading }] = useGetSalesLazyQuery({
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    errorPolicy: APOLLO_ERROR_POLICY.ALL,
  });

  useEffect(() => {
    const getSaleById = async () => {
      if (!orgId || !saleId) return;

      // Backend expects FilterGroups to be a List, so wrap in array
      const filterGroups = [
        {
          filters: [
            {
              field: FieldOption.societyId.value as SaleFilterField,
              operator: OperatorOption.equals.value as ComparisonOperator,
              value: orgId,
            },
            {
              field: FieldOption.saleId.value as SaleFilterField,
              operator: OperatorOption.equals.value as ComparisonOperator,
              value: saleId,
            },
          ],
          groups: [],
          logicalOperator: LogicalOperator.and,
        },
      ] as FilterGroupOfSaleFilteredFilterOptionInput[];
      getSales({
        variables: {
          requestInput: {
            pagination: {
              currentPage: 1,
              pageSize: 1,
            },
            filterGroups,
            sortOptions: [],
          },
        },
        context: {
          headers: setDefaultHeader(orgId),
        },
      });
    };

    getSaleById();
  }, [orgId, saleId, getSales]);

  return { data: data?.sales.items?.[0] || null, loading };
}
